import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, videos } from '@/mocks/qieziData';

const categoryJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '茄子视频 - 视频分类',
  description: '浏览茄子视频全部分类内容，涵盖搞笑视频、生活记录、才艺展示、美食分享等丰富类别，发现你感兴趣的视频。',
  url: 'https://www.qiezitv.com/category',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://www.qiezitv.com/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': 'https://www.qiezitv.com/category', name: '视频分类' } },
    ],
  },
};

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'comedy';
  const [sortBy, setSortBy] = useState<'hot' | 'new'>('hot');

  const activeCategory = useMemo(() => categories.find((c) => c.id === activeCat) || categories[0], [activeCat]);

  const filteredVideos = useMemo(() => {
    let result = videos.filter((v) => v.category === activeCat);
    if (sortBy === 'new') {
      result = [...result].sort((a, b) => b.publishDate.localeCompare(a.publishDate));
    }
    return result;
  }, [activeCat, sortBy]);

  const handleCatChange = (catId: string) => {
    setSearchParams({ cat: catId });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '视频分类' }]} />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">视频分类</h1>
              <p className="mt-2 text-sm text-foreground-500">探索你感兴趣的视频内容，发现更多精彩</p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCatChange(cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeCat === cat.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                  }`}
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Active Category Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
              <div>
                <h2 className="text-xl font-bold text-foreground-900 flex items-center gap-2">
                  <i className={`${activeCategory.icon} w-6 h-6 flex items-center justify-center text-primary-500`}></i>
                  {activeCategory.name}
                </h2>
                <p className="text-sm text-foreground-500 mt-1">共 {activeCategory.count.toLocaleString()} 个视频</p>
              </div>

              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setSortBy('hot')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all ${
                    sortBy === 'hot' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500'
                  }`}
                >
                  最热
                </button>
                <button
                  onClick={() => setSortBy('new')}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all ${
                    sortBy === 'new' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500'
                  }`}
                >
                  最新
                </button>
              </div>
            </div>

            {/* Video Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 pb-16">
              {filteredVideos.length > 0 ? (
                filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02] border border-background-200/70"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {video.title}
                      </h3>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <img src={video.avatar} alt={video.author} className="w-6 h-6 rounded-full object-cover" />
                          <span className="text-xs text-foreground-500">{video.author}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                            {video.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-heart-line w-3 h-3 flex items-center justify-center"></i>
                            {video.likes}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-full text-center py-16">
                  <p className="text-foreground-500">该分类暂无视频内容</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}